import { useEffect, useState } from 'react'
import { Pencil } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useSystemSettings,
  useUpdateSystemSetting,
} from '@/hooks/useAdminSystemSettings'
import type { SystemSettingRead } from '@/api/adminSystemSettings'
import { formatDateTime } from '@/utils/formatters'
import { formatFieldName } from '@/utils/auditTrailFormatters'

function formatSettingLabel(setting: SystemSettingRead): string {
  return setting.description || formatFieldName(setting.setting_key)
}

export default function AdminSystemSettings() {
  const { data, isLoading, isError } = useSystemSettings()
  const updateMutation = useUpdateSystemSetting()

  const [editOpen, setEditOpen] = useState(false)
  const [editingSetting, setEditingSetting] =
    useState<SystemSettingRead | null>(null)
  const [editValue, setEditValue] = useState('')
  const [validationError, setValidationError] = useState('')

  useEffect(() => {
    if (!editOpen) {
      return
    }
    setEditValue(editingSetting?.value ?? '')
    setValidationError('')
  }, [editOpen, editingSetting])

  const handleEdit = (setting: SystemSettingRead) => {
    setEditingSetting(setting)
    setEditOpen(true)
  }

  const handleSave = () => {
    const trimmed = editValue.trim()

    if (!trimmed) {
      setValidationError('Value is required.')
      return
    }

    const parsed = Number(trimmed)
    if (Number.isNaN(parsed) || parsed < 0) {
      setValidationError('Value must be a non-negative number.')
      return
    }

    if (!editingSetting) {
      return
    }

    updateMutation.mutate(
      { key: editingSetting.setting_key, value: trimmed },
      {
        onSuccess: () => {
          setEditOpen(false)
          setEditingSetting(null)
        },
      }
    )
  }

  const settings = data?.items ?? []

  return (
    <>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Edit System Setting</DialogTitle>
            <DialogDescription>
              {editingSetting
                ? formatSettingLabel(editingSetting)
                : 'Update the setting value.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="setting-value">Value</Label>
              <Input
                id="setting-value"
                value={editValue}
                onChange={e => {
                  setEditValue(e.target.value)
                  setValidationError('')
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSave()
                  }
                }}
                disabled={updateMutation.isPending}
                placeholder="Enter a numeric value"
              />
              {validationError && (
                <p className="text-sm text-destructive">{validationError}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditOpen(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={updateMutation.isPending || !editValue.trim()}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col gap-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            System Settings
          </h1>
          <p className="mt-1 text-base text-muted-foreground">
            Default values applied to newly created plans.
          </p>
        </div>

        <Alert>
          <AlertDescription>
            Changes apply to newly created plans only. Existing plans are not
            affected.
          </AlertDescription>
        </Alert>

        {isLoading ? (
          <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
            Loading system settings...
          </div>
        ) : isError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
            Failed to load system settings. Please try again.
          </div>
        ) : settings.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
            No system settings found.
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Setting</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {settings.map(setting => (
                  <TableRow key={setting.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {formatSettingLabel(setting)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {setting.setting_key}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">{setting.value}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(setting.updated_at) ?? '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleEdit(setting)}
                          aria-label={`Edit ${setting.setting_key}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </>
  )
}
